import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * Custom hook for managing popup window functionality
 * Based on the implementation from AIAssistantChatV2
 */
export function usePopoutWindow(windowName = 'popout-window') {
  const [isPopped, setIsPopped] = useState(false);
  const [popupWindow, setPopupWindow] = useState<Window | null>(null);
  const popupRef = useRef<Window | null>(null);

  const openPopup = useCallback(
    (title = 'Popout Window') => {
      if (popupRef.current && !popupRef.current.closed) {
        // Window already exists, just focus it
        popupRef.current.focus();
        return;
      }

      const popup = window.open(
        '',
        windowName,
        'width=1000,height=700,scrollbars=yes,resizable=yes,status=no,toolbar=no,menubar=no',
      );

      if (popup) {
        popupRef.current = popup;
        setPopupWindow(popup);
        setIsPopped(true);

        // Set up the popup window HTML structure
        popup.document.title = title;
        popup.document.head.innerHTML = `
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1">
        <title>${title}</title>
      `;

        // Copy stylesheets from parent window
        const stylesheets = Array.from(
          document.head.querySelectorAll('link[rel="stylesheet"], style'),
        );
        stylesheets.forEach((sheet) => {
          const clone = sheet.cloneNode(true);
          popup.document.head.appendChild(clone);
        });

        // Create container for React content
        popup.document.body.innerHTML =
          '<div id="popup-root" style="margin: 0; padding: 0; height: 100vh; width: 100vw; overflow: hidden;"></div>';

        // Handle popup window close
        popup.addEventListener('beforeunload', () => {
          setIsPopped(false);
          setPopupWindow(null);
          popupRef.current = null;
        });

        // Handle parent window close/reload
        window.addEventListener('beforeunload', () => {
          if (popupRef.current && !popupRef.current.closed) {
            popupRef.current.close();
          }
        });
      }
    },
    [windowName],
  );

  const closePopup = useCallback(() => {
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
    }
    setIsPopped(false);
    setPopupWindow(null);
    popupRef.current = null;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
      }
    };
  }, []);

  return {
    isPopped,
    popupWindow,
    openPopup,
    closePopup,
  };
}
